import {ButtonGroup, Button, Form, FormButton, FormField} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {useGetOperationItemsQuery} from '@dms/api/finance';
import {usePostArticleAssignWorkItemGroupMutation} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

interface WorkItemGroupDialogProps {
  articleIds: string[];
  onClose: () => void;
  onSuccess: () => void;
}

const workItemGroupFormSchema = z.object({
  workItemGroupId: z.string().min(1),
});

type WorkItemGroupForm = z.infer<typeof workItemGroupFormSchema>;

export function WorkItemGroupDialog(props: WorkItemGroupDialogProps) {
  const {data: operationItems, isLoading} = useGetOperationItemsQuery({
    application: 'workshop',
    itemGroup: 'material',
  });

  const [assignWorkItemGroup] = usePostArticleAssignWorkItemGroupMutation();

  const handleSubmit = async (values: WorkItemGroupForm) => {
    await assignWorkItemGroup({
      articleIds: props.articleIds,
      workItemGroupId: values.workItemGroupId,
    })
      .unwrap()
      .then(() => {
        props.onClose();
        props.onSuccess();
      })
      .catch(handleApiError);
  };

  const options =
    operationItems?.map((item) => ({
      label: item.itemCodeName,
      value: item.operationId,
    })) ?? [];

  return (
    <Form onSubmit={handleSubmit} experimentalZodSchema={workItemGroupFormSchema}>
      {(control) => (
        <VStack spacing={4}>
          <FormField
            menuInPortal
            control={control}
            name="workItemGroupId"
            type="choice"
            label={i18n.t('entity.addWork.labels.workItemGroup')}
            isRequired
            isLoading={isLoading}
            options={options}
          />

          <ButtonGroup align="right">
            <Button
              title={i18n.t('general.actions.discard')}
              variant="secondary"
              onClick={props.onClose}
            />
            <FormButton control={control} type="submit" title={i18n.t('general.actions.confirm')} />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
